import { Link } from 'react-router-dom';
import { ArrowRight, Brain, Code, Zap, Rocket } from 'lucide-react';
import EventBanner from '../ui/EventBanner';
import PromotionalBanner from '../ui/PromotionalBanner';
import { useSmoothScroll } from '../../hooks/useScrollAnimation';

// Toggle between Event Banner and Promotional Banner
// Set to false after Jan 3rd, 2026 to revert to promotional banner
const SHOW_EVENT_BANNER = false;

const HeroSection = () => {
  const { scrollToSection } = useSmoothScroll();

  const handleScrollToCourses = (e: React.MouseEvent) => {
    e.preventDefault();
    scrollToSection('course-recommendations', 100);
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center px-4 overflow-visible pt-16 bg-gradient-to-br from-gray-50 to-green-50" id="main">
      {/* Animated Background */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-4 md:left-20 w-48 h-48 md:w-72 md:h-72 bg-green-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-4 md:right-20 w-64 h-64 md:w-96 md:h-96 bg-emerald-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-48 h-48 md:w-64 md:h-64 bg-teal-500/10 rounded-full blur-3xl animate-pulse delay-500"></div>
      </div>

      {/* Content */}
      <div className={`relative ${SHOW_EVENT_BANNER ? 'max-w-7xl' : 'max-w-6xl'} mx-auto ${SHOW_EVENT_BANNER ? '' : 'text-center'} text-gray-900 animate-fade-in`}>
        {/* Toggle between Event Banner and Promotional Banner */}
        <div className={SHOW_EVENT_BANNER ? 'mb-8 md:mb-12' : 'mb-6 md:mb-8'}>
          {SHOW_EVENT_BANNER ? (
            <EventBanner 
              variant="hero"
              className="w-full"
            />
          ) : (
            <PromotionalBanner 
              variant="hero"
              className="max-w-4xl mx-auto"
            />
          )}
        </div>

        {/* New Course Alert */}
        <div className="mb-8 md:mb-10 animate-fade-in">
          <a
            href="https://www.udemy.com/course/ai-red-teaming-promptfoo-rag-chatbots-agents/"
            target="_blank"
            rel="noopener noreferrer"
            className="group relative block max-w-4xl mx-auto bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 rounded-2xl p-1 shadow-2xl hover:shadow-purple-500/30 transition-all duration-300 transform hover:scale-[1.02]"
          >
            <div className="relative bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 rounded-xl p-4 md:p-6">
              {/* Animated background glow */}
              <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/20 via-transparent to-cyan-400/20 rounded-xl animate-pulse pointer-events-none"></div>
              
              <div className="relative flex items-center gap-3 md:gap-6">
                {/* New Badge */}
                <div className="flex-shrink-0">
                  <div className="bg-yellow-400 text-indigo-900 px-3 py-1.5 rounded-full font-bold text-xs md:text-sm shadow-lg animate-pulse flex items-center gap-1">
                    <Rocket className="w-3 h-3 md:w-4 md:h-4" />
                    NEW
                  </div>
                </div>

                {/* Course Info */}
                <div className="flex-1 min-w-0 text-left">
                  <h3 className="text-white font-bold text-sm md:text-lg leading-tight mb-1 group-hover:text-yellow-200 transition-colors">
                    Red Teaming AI Apps with Promptfoo: RAG, Chatbots & Agents
                  </h3>
                  <div className="flex flex-wrap items-center gap-1.5 md:gap-2 text-xs md:text-sm text-white/80">
                    <span className="bg-white/20 px-2 py-0.5 rounded-full">Promptfoo</span>
                    <span className="text-white/50">•</span>
                    <span>AI Red Teaming</span>
                    <span className="text-white/50">•</span>
                    <span>OWASP</span>
                    <span className="text-white/50">•</span>
                    <span>RAG Security</span>
                    <span className="text-white/50">•</span>
                    <span>Jailbreak Testing</span>
                  </div>
                </div>

                {/* CTA */}
                <div className="flex-shrink-0 hidden sm:block">
                  <div className="bg-white text-purple-700 px-4 py-2 rounded-full font-bold text-sm shadow-lg group-hover:bg-yellow-300 group-hover:text-indigo-900 transition-all transform group-hover:scale-105 flex items-center gap-1 whitespace-nowrap">
                    Enroll Now
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </div>
            </div>
          </a>
        </div>

        <div className="text-center">
          {/* Badge */}
          <div className="flex justify-center mb-4 md:mb-6">
            <div className="flex items-center space-x-3 md:space-x-4 bg-white/80 backdrop-blur-sm rounded-full px-4 py-2 md:px-6 md:py-3 border border-green-200 animate-scale-in shadow-lg">
              <Brain className="w-5 h-5 md:w-6 md:h-6 text-green-600" aria-label="Brain icon" />
              <span className="text-green-600 font-semibold text-sm md:text-base">AI-Powered Testing Mastery Program</span>
            </div>
          </div>

          {/* Main Headline */}
          <h1 className="relative text-[2.5rem] sm:text-[3rem] md:text-[5rem] lg:text-[6rem] font-bold leading-[1.1] mb-8 md:mb-12 bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 bg-clip-text text-transparent z-10 animate-slide-up">
            Master AI Testing
            <br />
            <span className="text-[2rem] sm:text-[2.5rem] md:text-[4rem] lg:text-[5rem]">End-to-End</span>
          </h1>

          {/* Subheadlines */}
          <p className="text-lg md:text-xl lg:text-2xl mb-3 md:mb-4 text-gray-700 animate-fade-in delay-300">
            AI QA Engineering: Foundation to Advanced
          </p>
          <p className="text-base md:text-lg mb-8 md:mb-12 text-gray-600 max-w-3xl mx-auto leading-relaxed animate-fade-in delay-500 px-4">
            From GenAI fundamentals and AI security to multi-agent systems. Master the complete spectrum of AI in software testing with our structured 9-course learning path.
          </p>

          {/* Call-to-action buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 md:gap-6 mb-12 md:mb-16 animate-fade-in delay-700 px-4">
            <button
              onClick={handleScrollToCourses}
              className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white px-6 md:px-8 py-3 md:py-4 text-base md:text-lg font-semibold rounded-full shadow-2xl hover:shadow-green-500/25 transition-all duration-300 transform hover:scale-105 w-full sm:w-auto flex items-center justify-center cursor-pointer"
            >
              Start Your AI Journey
              <ArrowRight className="ml-2 w-4 h-4 md:w-5 md:h-5" aria-label="ArrowRight icon" />
            </button>
            <Link
              to="/paths"
              className="bg-white border-2 border-green-500 text-green-600 hover:bg-green-50 px-6 md:px-8 py-3 md:py-4 text-base md:text-lg font-semibold rounded-full shadow-lg hover:shadow-green-500/25 transition-all duration-300 transform hover:scale-105 w-full sm:w-auto"
            >
              View Learning Path
            </Link>
          </div>

          {/* Stats/Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mt-12 md:mt-16 px-4">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 md:p-6 border border-green-200 hover:border-green-500/50 transition-all duration-200 hover:scale-[1.02] animate-fade-in shadow-lg">
              <Code className="w-10 h-10 md:w-12 md:h-12 text-green-600 mb-3 md:mb-4 mx-auto" aria-label="Code icon" />
              <h3 className="text-lg md:text-xl font-bold mb-2 text-gray-900">9 Complete Courses</h3>
              <p className="text-gray-600 text-sm md:text-base">Structured learning path from basics to advanced</p>
            </div>
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 md:p-6 border border-green-200 hover:border-emerald-500/50 transition-all duration-200 hover:scale-[1.02] animate-fade-in shadow-lg" style={{ animationDelay: '100ms' }}>
              <Brain className="w-10 h-10 md:w-12 md:h-12 text-emerald-600 mb-3 md:mb-4 mx-auto" aria-label="Brain icon" />
              <h3 className="text-lg md:text-xl font-bold mb-2 text-gray-900">AI & LLM Integration</h3>
              <p className="text-gray-600 text-sm md:text-base">Master GPT-4, Claude, HuggingFace & more</p>
            </div>
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 md:p-6 border border-green-200 hover:border-teal-500/50 transition-all duration-200 hover:scale-[1.02] animate-fade-in shadow-lg" style={{ animationDelay: '200ms' }}>
              <Zap className="w-10 h-10 md:w-12 md:h-12 text-teal-600 mb-3 md:mb-4 mx-auto" aria-label="Zap icon" />
              <h3 className="text-lg md:text-xl font-bold mb-2 text-gray-900">Career Transformation</h3>
              <p className="text-gray-600 text-sm md:text-base">From QA to AI-powered testing expert</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
